import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * Visual Semantics Infographic Viewer Component
 * Renders an accessible figure with responsive SVG graphic and editorial caption.
 */
export function InfographicViewer({ src, alt, title, caption }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <figure className={`infographic-container ${isExpanded ? "is-expanded" : ""}`} role="figure" aria-label={title}>
      <div className="infographic-header">
        <span className="infographic-badge">Infografía Editorial</span>
        <h3 className="infographic-title">{title}</h3>
      </div>
      <div
        className={`infographic-media-box ${isExpanded ? "is-expanded" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={isExpanded ? "Reducir tamaño del gráfico" : "Ampliar tamaño del gráfico"}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="infographic-img"
          width="900"
          height="520"
        />
      </div>
      <figcaption className="infographic-caption">
        <strong>Conclusión del análisis:</strong> {caption}
        <button
          type="button"
          className="infographic-expand-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? "Reducir gráfico" : "Ampliar gráfico"}
        </button>
      </figcaption>
    </figure>
  );
}

/**
 * Editorial photograph with an explicit caption and reserved dimensions.
 * The component is shared by cards and detail pages so catalog imagery stays
 * accessible, responsive, and CLS-safe across the whole site.
 */
export function EditorialImage({ src, alt, caption, compact = false }) {
  if (!src) {
    return null;
  }

  return (
    <figure className={`editorial-image ${compact ? "editorial-image--compact" : ""}`}>
      <img src={src} alt={alt} loading="lazy" width="1536" height="1024" />
      {caption ? <figcaption>{caption}</figcaption> : null}
    </figure>
  );
}

/**
 * Interactive Decision Selector for Comparisons
 * Allows users to click on their objective and receive dynamic recommendation.
 */
export function ComparisonDecisionSelector({ scenarios = [], entities = [] }) {
  const [selectedIdx, setSelectedIdx] = useState(0);

  if (!scenarios || scenarios.length === 0) {
    return null;
  }

  const current = scenarios[selectedIdx] || scenarios[0];
  const winner = current?.winner || (current?.winner_entity_id && entities.find(e => e.id === current.winner_entity_id));

  return (
    <div className="interactive-decision-widget" role="region" aria-label="Selector Interactivo de Perfil y Elección">
      <div className="widget-header">
        <span className="widget-eyebrow">Asistente de Decisión</span>
        <h3>¿Cuál se adapta mejor a tu objetivo de estudio?</h3>
        <p>Selecciona tu situación actual para ver la recomendación técnica fundamentada:</p>
      </div>

      <div className="scenario-pills-row" role="tablist">
        {scenarios.map((item, idx) => (
          <button
            key={item.scenario}
            type="button"
            role="tab"
            aria-selected={idx === selectedIdx}
            className={`decision-pill-btn ${idx === selectedIdx ? "is-active" : ""}`}
            onClick={() => setSelectedIdx(idx)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                setSelectedIdx((idx + 1) % scenarios.length);
              } else if (e.key === "ArrowLeft") {
                setSelectedIdx((idx - 1 + scenarios.length) % scenarios.length);
              }
            }}
          >
            {item.scenario}
          </button>
        ))}
      </div>

      <div className="decision-result-card" role="status" aria-live="polite">
        <div className="decision-result-header">
          <span className="recommendation-tag">Opción más adecuada:</span>
          <h4>{winner?.title || winner?.name || "Opción recomendada"}</h4>
        </div>
        <p className="decision-reason">{current.reason}</p>
        {winner?.path && (
          <div className="decision-cta-box">
            <Link to={winner.path} className="button button-primary">
              Ver detalles de {winner.title || winner.name}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Dimension Scorecard comparing key institutional/program dimensions side by side.
 */
export function DimensionScorecard({ entityA, entityB, scores = [] }) {
  if (!scores || scores.length === 0) {
    return null;
  }

  return (
    <div className="dimension-scorecard-panel">
      <div className="scorecard-header">
        <span className="widget-eyebrow">Evaluación Comparativa de Dimensiones</span>
        <h3>Rigor Técnico, Acompañamiento y Calidad de Plataforma</h3>
      </div>

      <div className="scorecard-legends">
        <div className="legend-item legend-a">
          <span className="legend-dot dot-a" />
          <strong>{entityA?.title || entityA?.name || "Opción A"}</strong>
        </div>
        <div className="legend-item legend-b">
          <span className="legend-dot dot-b" />
          <strong>{entityB?.title || entityB?.name || "Opción B"}</strong>
        </div>
      </div>

      <div className="scorecard-rows">
        {scores.map((row) => (
          <div className="score-row" key={row.criterion}>
            <div className="score-label-row">
              <span className="criterion-name">{row.criterion}</span>
              <span className="score-values">
                <span className="val-a">{row.scoreA}/10</span> vs{" "}
                <span className="val-b">{row.scoreB}/10</span>
              </span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill-a"
                style={{ width: `${row.scoreA * 10}%` }}
                title={`${entityA?.title}: ${row.scoreA}/10`}
              />
              <div
                className="bar-fill-b"
                style={{ width: `${row.scoreB * 10}%` }}
                title={`${entityB?.title}: ${row.scoreB}/10`}
              />
            </div>
            {row.note && <p className="criterion-note">{row.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Visual Top-3 Ranking Podium for Listicle pages
 */
export function ListiclePodium({ items = [] }) {
  if (!items || items.length < 2) {
    return null;
  }

  const top3 = items.slice(0, 3);
  const medalColors = ["#E97F4F", "#0E7C66", "#3B82F6"];
  const rankLabels = ["1° Lugar", "2° Lugar", "3° Lugar"];

  return (
    <div className="podium-container" role="region" aria-label="Destacados del Ranking">
      <div className="podium-heading">
        <span className="widget-eyebrow">Selección Editorial</span>
        <h3>Los 3 Programas e Instituciones Líderes</h3>
      </div>
      <div className="podium-grid">
        {top3.map((item, idx) => (
          <article
            className={`podium-card rank-${idx + 1}`}
            key={item.entity?.id || item.position}
            style={{ borderColor: medalColors[idx] }}
          >
            <div className="podium-rank-badge" style={{ backgroundColor: medalColors[idx] }}>
              {rankLabels[idx]}
            </div>
            <h4 className="podium-title">
              <Link to={item.entity?.path || "#"}>{item.entity?.title}</Link>
            </h4>
            <p className="podium-highlight">{item.why_it_made_the_list}</p>
            <div className="podium-best-for">
              <strong>Ideal para:</strong> {item.best_for}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

/**
 * Trust & Editorial Signals Grid
 */
export function TrustSignalsGrid() {
  const signals = [
    {
      icon: "🔎",
      title: "Fuente oficial enlazada",
      desc: "La ficha invita a contrastar la propuesta con la institución o programa de origen antes de decidir.",
    },
    {
      icon: "🧭",
      title: "Criterios comparables",
      desc: "Modalidad, nivel, duración y área se presentan como señales de orientación para la búsqueda.",
    },
    {
      icon: "📊",
      title: "Contexto para decidir",
      desc: "Las matrices y mapas visuales resumen diferencias para que puedas profundizar en la ficha correspondiente.",
    },
    {
      icon: "✅",
      title: "Verificación final",
      desc: "Aranceles, fechas, requisitos y validez deben confirmarse en la fuente oficial vigente.",
    },
  ];

  return (
    <div className="trust-signals-panel">
      <div className="trust-signals-grid">
        {signals.map((sig) => (
          <div className="trust-signal-card" key={sig.title}>
            <span className="trust-icon" aria-hidden="true">{sig.icon}</span>
            <div className="trust-content">
              <strong>{sig.title}</strong>
              <p>{sig.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
